import { useState } from "react";
import { ArrowUp, ArrowDown, ChevronsUpDown } from "lucide-react";

/**
 * Uniform, click-to-sort table header + helpers, mirroring the Prodotti /
 * Download pages so every table sorts the same way (asc ⇄ desc on click).
 *
 * Usage:
 *   const { sortField, sortDir, toggleSort } = useTableSort();
 *   <SortableTh field="name" label="Nome" sortField={sortField}
 *               sortDir={sortDir} onSort={toggleSort} className="px-3 py-2 font-normal" />
 *   const sorted = sortRows(rows, sortField, sortDir, { name: r => r.name });
 */
export function useTableSort(defaultField = "", defaultDir = "asc") {
  const [sortField, setSortField] = useState(defaultField);
  const [sortDir, setSortDir] = useState(defaultDir);
  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => (d === "asc" ? "desc" : "asc"));
    else { setSortField(field); setSortDir("asc"); }
  };
  return { sortField, sortDir, toggleSort };
}

// Client-side sorter. `getters` maps field -> fn(row) returning a comparable
// value (string / number / timestamp). Falls back to row[field].
export function sortRows(rows, sortField, sortDir, getters = {}) {
  if (!sortField) return rows;
  const get = getters[sortField] || ((r) => r[sortField]);
  const dir = sortDir === "desc" ? -1 : 1;
  return [...rows].sort((a, b) => {
    let va = get(a), vb = get(b);
    if (va == null) va = "";
    if (vb == null) vb = "";
    if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
    return String(va).localeCompare(String(vb), "it", { numeric: true, sensitivity: "base" }) * dir;
  });
}

export function SortableTh({ field, label, sortField, sortDir, onSort, className = "", align = "left" }) {
  const active = sortField === field;
  const Icon = !active ? ChevronsUpDown : (sortDir === "asc" ? ArrowUp : ArrowDown);
  return (
    <th onClick={() => onSort(field)}
        className={`cursor-pointer select-none group ${className} ${active ? "text-[#1A1A1A]" : ""}`}
        data-testid={`th-sort-${field}`}
        title={`Ordina per ${label}${active ? (sortDir === "asc" ? " (crescente)" : " (decrescente)") : ""}`}>
      <span className={`inline-flex items-center gap-1.5 ${align === "right" ? "w-full justify-end" : ""}`}>
        <span>{label}</span>
        <Icon className={`w-3 h-3 transition ${active ? "text-[#1A1A1A]" : "text-[#C5B39B] opacity-70 group-hover:opacity-100"}`} strokeWidth={2} />
      </span>
    </th>
  );
}

export default SortableTh;
